const NEW_RELIC_ENABLED_FLAG = '__TBX_NEW_RELIC_ENABLED__CONFIG__';

/**
 * True when /configs (appSettings) includes a truthy newRelicEnabled flag.
 */
export function isNewRelicConfigEnabled(appSettings) {
  return Boolean(appSettings?.newRelicEnabled);
}

/**
 * Starts or stops Browser Agent network harvest based on Unity /configs.
 * Prefers consent() (agent >= 1.303 with browser_consent_mode enabled).
 * Falls back to aborting harvests on older agents when disabled.
 */
function applyNewRelicAgentReporting(enabled) {
  if (!window.newrelic) {
    return;
  }

  if (typeof window.newrelic.consent === 'function') {
    console.log('applyNewRelicAgentReporting', enabled);
    window.newrelic.consent(enabled);
    return;
  }

  if (enabled) {
    return;
  }

  try {
    const nreum = window.NREUM;
    const agents = nreum?.initializedAgents;

    if (agents) {
      Object.values(agents).forEach((agent) => {
        if (agent?.runtime) {
          agent.runtime.disabled = true;
        }
        agent?.ee?.abort?.();
      });
    }

    nreum?.ee?.abort?.();

    if (nreum?.info) {
      nreum.info.beacon = '';
      nreum.info.errorBeacon = '';
    }
  } catch (e) {
    // Best-effort shutdown for agents without consent mode.
  }
}

/**
 * Syncs the runtime reporting flag from Unity /configs (appSettings).
 * Agent load is controlled by env-config + public/newrelic/load.js;
 * harvest + custom reporting also require newRelicEnabled.
 */
export function syncNewRelicEnabledFlag(appSettings) {
  const enabled = isNewRelicConfigEnabled(appSettings);
  console.log('syncNewRelicEnabledFlag', enabled);
  window[NEW_RELIC_ENABLED_FLAG] = enabled;
  applyNewRelicAgentReporting(enabled);
}

export function canReportToNewRelic() {
  return Boolean(window.newrelic && window[NEW_RELIC_ENABLED_FLAG]);
}

/**
 * Applies nrMaxEventPoolSize from Unity /configs via newrelic.setMaxEventPoolSize.
 */
export function applyNewRelicMaxEventPoolSize(appSettings) {
  if (!canReportToNewRelic()) {
    return;
  }

  const maxEventPoolSize = Number(appSettings?.nrMaxEventPoolSize);
  if (!Number.isFinite(maxEventPoolSize) || maxEventPoolSize <= 0) {
    return;
  }

  if (typeof window.newrelic.setMaxEventPoolSize === 'function') {
    window.newrelic.setMaxEventPoolSize(maxEventPoolSize);
  }
}

export function parseErrorToNewRelic(inError) {
  if (!canReportToNewRelic()) {
    return;
  }

  let parsedError = {
    message: '',
    stack: '',
    lineNumber: '',
    fileName: '',
    columnNumber: '',
    name: '',
  };

  if (typeof inError === 'string') {
    parsedError.message = inError;
    window.newrelic.noticeError(parsedError);
    return;
  }

  if (typeof inError === 'object') {
    parsedError = { ...parsedError, ...inError };

    if (inError.code) {
      parsedError.message = `UNITY ERROR: [${inError.code}]`;
    }

    if (inError.componentStack) {
      parsedError.stack = inError.componentStack;
    }

    window.newrelic.noticeError(parsedError);
  }
}
